import React from 'react';
import {
  Box,
  Card,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import useIsMobile from '../../hooks/useIsMobile';

export interface TourRecapCardProps {
  headline?: string | null;
  highlights?: string | null;
  redFlags?: string[];
  followUps?: string[];
  /** Shown under the overline when the card is not already on the property's own page. */
  address?: string | null;
  /** The walkthrough is submitted but Reva has not written the recap yet. */
  pending?: boolean;
  /** Tighter rendering for the property rows in the buying journey. */
  compact?: boolean;
}

const OVERLINE = 'Reva’s tour recap';

/**
 * Reva's read-back of a tour the buyer walked: the headline, what stood out, what they
 * flagged, and the questions worth asking the listing side. Only ever rendered for the
 * buyer who wrote the walkthrough — the API blanks these fields for everyone else.
 */
const TourRecapCard: React.FC<TourRecapCardProps> = ({
  headline,
  highlights,
  redFlags = [],
  followUps = [],
  address,
  pending = false,
  compact = false,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  if (!pending && !headline) return null;

  const primary = theme.palette.primary.main;
  const padding = compact ? 1.5 : { xs: 2, sm: 2.5 };

  const renderList = (
    items: string[],
    label: string,
    color: string,
    icon: React.ReactNode
  ) => (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Typography
          variant="overline"
          sx={{ color, fontWeight: 600, letterSpacing: 1, lineHeight: 1.6 }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack component="ul" spacing={0.5} sx={{ pl: 3, m: 0, mt: 0.25 }}>
        {items.map((item) => (
          <Typography
            key={item}
            component="li"
            variant="body2"
            color="text.secondary"
          >
            {item}
          </Typography>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Card
      elevation={0}
      sx={{
        p: padding,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={compact ? 1 : 1.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: compact ? 32 : 40,
              height: compact ? 32 : 40,
              flexShrink: 0,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(primary, 0.12),
              color: primary,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: compact ? 18 : 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: primary,
                fontWeight: 600,
                letterSpacing: 1,
                lineHeight: 1.6,
              }}
            >
              {OVERLINE}
              {address ? ` · ${address}` : ''}
            </Typography>
            {pending ? (
              <Typography
                variant={compact ? 'body2' : 'subtitle1'}
                fontWeight={600}
              >
                Writing your recap&hellip;
              </Typography>
            ) : (
              <Typography
                variant={compact ? 'body2' : 'subtitle1'}
                fontWeight={600}
                sx={{ lineHeight: 1.4 }}
              >
                {headline}
              </Typography>
            )}
          </Box>
        </Stack>

        {pending && (
          <Box sx={{ pl: compact ? 0 : { xs: 0, sm: 6.5 } }}>
            <Typography variant="body2" color="text.secondary">
              Reva is turning your reactions, notes, and photos into a recap. It
              usually lands within a few minutes &mdash; this page updates on
              its own.
            </Typography>
            <Skeleton width={isMobile ? '90%' : '70%'} />
            <Skeleton width={isMobile ? '70%' : '45%'} />
          </Box>
        )}

        {!pending && (
          <Stack spacing={1.5} sx={{ pl: compact ? 0 : { xs: 0, sm: 6.5 } }}>
            {highlights && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.6,
                  ...(compact
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                    : {}),
                }}
              >
                {highlights}
              </Typography>
            )}
            {redFlags.length > 0 &&
              renderList(
                compact ? redFlags.slice(0, 2) : redFlags,
                'What you flagged',
                theme.palette.error.main,
                <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            {!compact &&
              followUps.length > 0 &&
              renderList(
                followUps,
                'Worth asking the listing side',
                primary,
                <LightbulbOutlinedIcon sx={{ fontSize: 16 }} />
              )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default TourRecapCard;
